import { yupResolver } from '@hookform/resolvers/yup';
import ArrowDown from '@images/svg-icons/arrowDown.svg';
import CloseIcon from '@images/svg-icons/closeIcon.svg';
import ErrorIcon from '@images/svg-icons/errorIcon.svg';
import SuccessIcon from '@images/svg-icons/successIcon.svg';
import WarningIcon from '@images/svg-icons/warning.svg';
import { InputAdornment, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import * as actions from '@redux-actions/';
import { getDoctorService } from '@services/myAppointmentServices';
import { getLabelFallback } from '@utils/utils';
import { SyntheticEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import { useDispatch, useSelector } from 'react-redux';
import Slider from 'react-slick';
import { RootState } from 'src/store/reducers';
import * as yup from 'yup';
import SearchDoctorByName from './SearchDoctorByName';
import SearchDoctorByPhone from './SearchDoctorByPhone';

interface Form {
    name: string;
    number: string;
}

type SearchDoctorModalProps = {
    doctor;
    setDoctor;
    close;
};

const SearchDoctorModal = (props: SearchDoctorModalProps) => {
    const [searchByPhone, setSearchByPhone] = useState(true);
    const [errorFormAdd, setErrorFormAdd] = useState(false);

    const config = useSelector((state: RootState) => state.common.config);
    const openModalAddDoctor = useSelector(
        (state: RootState) => state.myAppointment.openModalAddDoctor
    );
    const doctorList = useSelector((state: RootState) => state.myAppointment.doctorList);
    const getDoctorRequestStatus = useSelector(
        (state: RootState) => state.myAppointment.getDoctorRequestStatus
    );

    const dispatch = useDispatch();

    const schema = yup
        .object()
        .shape({
            name: yup.string(),
            number: yup.string(),
        })
        // @ts-ignore
        .atLeastOneOf(['name', 'number']);

    const defaultFormValues: Form = {
        name: '',
        number: '',
    };

    const methods = useForm<Form>({
        resolver: yupResolver(schema),
        mode: 'onBlur',
        defaultValues: defaultFormValues,
    });

    const {
        register,
        control,
        handleSubmit,
        setValue,
        clearErrors,
        setError,
        watch,
        formState: { errors, isSubmitting },
    } = methods;

    const watchName = watch('name');
    const watchNumber = watch('number');

    const initialFormStatus = {
        name: {
            focus: false,
            success: false,
        },
        number: {
            focus: false,
            success: false,
        },
    };

    const carouselSettings = {
        dots: true,
        arrows: false,
        speed: 0,
        infinite: false,
        slidesToShow: 1,
        slidesToScroll: 1,
        initialSlide: 0,
        dotsClass: 'slick-dots slick-thumb',
        customPaging: i => <div className="dots"></div>,
    };

    const getDoctor = (phone, name, city, state) => {
        dispatch(actions.getDoctorSuccess(null));
        if (!!phone) {
            dispatch(
                getDoctorService(
                    config.mockRequest,
                    config.storeId,
                    phone.replace(/ /g, '').replace('(', '').replace(')', '').replace('-', '')
                )
            );
        } else {
            dispatch(
                getDoctorService(
                    config.mockRequest,
                    config.storeId,
                    name.replace(/ /g, '_') + '!' + state + '!' + city.replace(/ /g, '_')
                )
            );
        }
    };

    const chooseDoctor = doctor => {
        props.setDoctor(doctor);
        props.close();
    };

    const [formStatus, setFormStatus] = useState(initialFormStatus);

    const handleInputEvent = (e: SyntheticEvent) => {
        e.persist();
        const event = e.target as HTMLInputElement;

        if (e.type === 'focus') {
            clearErrors(event.name as keyof Form);
            setFormStatus({
                ...formStatus,
                [event.name]: { success: event.value.trim() !== '', focus: true },
            });
        }

        if (e.type === 'blur') {
            setFormStatus({
                ...formStatus,
                [event.name]: {
                    success: event.value.trim() !== '',
                    focus: false,
                },
            });
            setValue(event.name as keyof Form, event.value.trim());
            if (!event.value.trim()) {
                setError(event.name as keyof Form, {
                    type: 'required',
                    message: getLabelFallback(
                        'MYPROFILE_MY_PRESCRIPTIONS_GENERIC_FIELD_CANNOT_BE_EMPTY'
                    ),
                });
            }
        }
    };

    return (
        <div className={'search-doctor-modal' + (openModalAddDoctor ? ' add-modal' : '')}>
            {openModalAddDoctor ? (
                <div className="search-doctor-modal-add">
                    <div className="title">
                        {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_SEARCH_TITLE_ADD')}
                    </div>

                    <div
                        className="back-title"
                        onClick={() => dispatch(actions.setOpenModalAddDoctor(false))}
                    >
                        <ArrowDown /> {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_SEARCH_BACK')}
                    </div>
                    <div className="search-doctor-modal-add-text form-group">
                        <TextField
                            {...register('name')}
                            className={`${
                                errors?.name?.message
                                    ? 'form-field-error'
                                    : formStatus.name.success
                                    ? 'form-field-success'
                                    : ''
                            }`}
                            onChange={handleInputEvent}
                            onFocus={handleInputEvent}
                            onBlur={handleInputEvent}
                            name="name"
                            variant="outlined"
                            label={'Clinic or Doctor name'}
                            InputProps={{
                                endAdornment: formStatus.name.focus ? (
                                    <InputAdornment position="end">
                                        <CloseIcon
                                            className="cursor-pointer"
                                            onMouseDown={() => setValue('name', '')}
                                        />
                                    </InputAdornment>
                                ) : errors?.name?.message ? (
                                    <InputAdornment position="end">
                                        <ErrorIcon />
                                    </InputAdornment>
                                ) : formStatus.name.success ? (
                                    <InputAdornment position="end">
                                        <SuccessIcon />
                                    </InputAdornment>
                                ) : null,
                            }}
                        />

                        <NumberFormat
                            {...register('number')}
                            format={'(###) ###-####'}
                            customInput={TextField}
                            className={`${
                                errors?.number?.message
                                    ? 'form-field-error'
                                    : formStatus.number.success
                                    ? 'form-field-success'
                                    : ''
                            }`}
                            value={watchNumber}
                            onChange={handleInputEvent}
                            onFocus={handleInputEvent}
                            onBlur={handleInputEvent}
                            name="number"
                            variant="outlined"
                            label={'Doctor phone number'}
                            InputProps={{
                                endAdornment: formStatus.number.focus ? (
                                    <InputAdornment position="end">
                                        <CloseIcon
                                            className="cursor-pointer"
                                            onMouseDown={() => setValue('number', '')}
                                        />
                                    </InputAdornment>
                                ) : errors?.number?.message ? (
                                    <InputAdornment position="end">
                                        <ErrorIcon />
                                    </InputAdornment>
                                ) : formStatus.number.success ? (
                                    <InputAdornment position="end">
                                        <SuccessIcon />
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                    </div>

                    {errorFormAdd && (
                        <div className="error-form">
                            {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_FORM_ERROR_ADD')}
                        </div>
                    )}

                    <div className="search-doctor-modal_submit">
                        <button
                            className="primary-button button-size-medium"
                            onClick={e => {
                                e.preventDefault();
                                setErrorFormAdd(false);
                                if (watchName.trim() !== '' && watchNumber.trim() !== '') {
                                    props.setDoctor({
                                        name: watchName,
                                        phoneNumber: watchNumber,
                                    });
                                    dispatch(actions.setOpenModalAddDoctor(false));
                                    props.close();
                                } else {
                                    setErrorFormAdd(true);
                                    if (watchName.trim() === '') {
                                        setError('name' as keyof Form, {
                                            type: 'required',
                                            message: getLabelFallback(
                                                'MYPROFILE_MY_PRESCRIPTIONS_GENERIC_FIELD_CANNOT_BE_EMPTY'
                                            ),
                                        });
                                    }
                                    if (watchNumber.trim() === '') {
                                        setError('number' as keyof Form, {
                                            type: 'required',
                                            message: getLabelFallback(
                                                'MYPROFILE_MY_PRESCRIPTIONS_GENERIC_FIELD_CANNOT_BE_EMPTY'
                                            ),
                                        });
                                    }
                                }
                            }}
                        >
                            Add doctor
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="title">
                        {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_SEARCH_TITLE')}
                    </div>

                    <ToggleButtonGroup
                        value={searchByPhone}
                        exclusive
                        onChange={(event: React.MouseEvent<HTMLElement>, value: boolean) => {
                            if (value != null) setSearchByPhone(value);
                        }}
                        aria-label="text alignment"
                    >
                        <ToggleButton value={true} aria-label="search by phone number">
                            Phone number
                        </ToggleButton>
                        <ToggleButton value={false} aria-label="search by clinic or doctor name">
                            Clinic or Doctor name
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {searchByPhone ? (
                        <SearchDoctorByPhone getDoctor={getDoctor} />
                    ) : (
                        <SearchDoctorByName getDoctor={getDoctor} />
                    )}
                    <div
                        className={
                            'search-doctor-modal-container' +
                            (!!doctorList && doctorList.length ? ' doctor-list' : '')
                        }
                    >
                        {getDoctorRequestStatus === 'SUCCESS' && doctorList?.length > 0 ? (
                            <>
                                <div className="doctor-data-table-container">
                                    <div className="headers-row">
                                        <div className="doctor-name-header">
                                            <div className="doctor-name">DOCTOR NAME</div>
                                        </div>
                                        <div className="clinic-name-header">
                                            <div className="clinic-name">CLINIC NAME</div>
                                        </div>
                                        <div className="address-header">
                                            <div className="address">ADDRESS</div>
                                        </div>
                                    </div>
                                    <div className="table-body">
                                        {doctorList.map((element, i) => {
                                            return (
                                                <div key={i} className="values-row">
                                                    <div className="doctor-name-value">
                                                        <div className="full-name">
                                                            <div className="doctor-firstName-value">
                                                                <div className="firstName">
                                                                    {element.name}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="clinic-name-value">
                                                        <div className="clinic-name">
                                                            {element.officeName}
                                                        </div>
                                                    </div>
                                                    <div className="address-value">
                                                        <div className="address-street-city-and-phone-value-container">
                                                            <div className="address">
                                                                <div className="address-street-and-city-value-container">
                                                                    <div className="address-street-value">
                                                                        <div className="address-street">
                                                                            {element.address?.address1}
                                                                        </div>
                                                                    </div>
                                                                    <div className="address-city-value">
                                                                        <div className="address-city">
                                                                            {element.address?.city},{' '}
                                                                            {element.address?.state}{' '}
                                                                            <br />
                                                                            {element.address?.zipPostalCode},{' '}
                                                                            {element.address?.country}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="address-phone-value-container">
                                                                    <div className="address-phone-value">
                                                                        <div className="address-phone">
                                                                            {element.phoneNumber.replace('-', '')}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="doctor-choose">
                                                                <button
                                                                    className="primary-button-negative small"
                                                                    onClick={() => chooseDoctor(element)}
                                                                >
                                                                    {getLabelFallback(
                                                                        'MYPROFILE_MY_PRESCRIPTIONS_SEARCH_DOCTOR_CHOOSE'
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="doctor-data-table-container-mobile">
                                    <div className="doctor-add">
                                        {getLabelFallback(
                                            'MYPROFILE_MY_PRESCRIPTIONS_SEARCH_ADD_MOBILE_1'
                                        )}
                                        <a
                                            onClick={() =>
                                                dispatch(actions.setOpenModalAddDoctor(true))
                                            }
                                        >
                                            {getLabelFallback(
                                                'MYPROFILE_MY_PRESCRIPTIONS_SEARCH_ADD_MOBILE_2'
                                            )}
                                        </a>
                                    </div>
                                    <div className="table-body">
                                        <Slider {...carouselSettings}>
                                            {doctorList.map((element, i) => {
                                                return (
                                                    <div key={i} className="doctor-total-container">
                                                        <div className="doctor-info">
                                                            <div className="doctor-row name">
                                                                <div className="doctor-row-title">
                                                                    {getLabelFallback(
                                                                        'MYPROFILE_MY_PRESCRIPTIONS_SEARCH_DOCTOR_NAME'
                                                                    )}
                                                                </div>
                                                                <div className="doctor-row-value">
                                                                    {element.name}
                                                                </div>
                                                            </div>
                                                            <div className="doctor-row clinic">
                                                                <div className="doctor-row-title ">
                                                                    {getLabelFallback(
                                                                        'MYPROFILE_MY_PRESCRIPTIONS_SEARCH_CLINIC_NAME'
                                                                    )}
                                                                </div>
                                                                <div className="doctor-row-value">
                                                                    {element.officeName}
                                                                </div>
                                                            </div>
                                                            <div className="doctor-row address">
                                                                <div className="doctor-row-title">
                                                                    {getLabelFallback(
                                                                        'MYPROFILE_MY_PRESCRIPTIONS_SEARCH_ADDRESS'
                                                                    )}
                                                                </div>
                                                                <div className="doctor-row-value">
                                                                    <div className="address-street-city-and-phone-value-container">
                                                                        <div className="address-street-value">
                                                                            <div className="address-street">
                                                                                {
                                                                                    element.address
                                                                                        ?.address1
                                                                                }
                                                                            </div>
                                                                        </div>
                                                                        <div className="address-city-value">
                                                                            <div className="address-city">
                                                                                {element.address?.city},{' '}
                                                                                {element.address?.state}{' '}
                                                                                <br />
                                                                                {
                                                                                    element.address
                                                                                        ?.zipPostalCode
                                                                                },{' '}
                                                                                {element.address?.country}
                                                                            </div>
                                                                        </div>
                                                                        <div className="address-phone-value">
                                                                            <div className="address-phone">
                                                                                {element.phoneNumber.replace('-', '')}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="doctor-choose">
                                                                        <button
                                                                            className="primary-button-negative small"
                                                                            onClick={() =>
                                                                                chooseDoctor(element)
                                                                            }
                                                                        >
                                                                            {getLabelFallback(
                                                                                'MYPROFILE_MY_PRESCRIPTIONS_SEARCH_DOCTOR_CHOOSE'
                                                                            )}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </Slider>
                                    </div>
                                </div>
                            </>
                        ) : getDoctorRequestStatus === 'ERROR' || doctorList?.length === 0 ? (
                            <div className="search-doctor-modal-container-error">
                                <div className="image">
                                    <WarningIcon />
                                </div>
                                <div>
                                    {getLabelFallback(
                                        'MYPROFILE_MY_PRESCRIPTIONS_SEARCH_DOCTOR_ERROR_1'
                                    )}
                                </div>
                                <div>
                                    {getLabelFallback(
                                        'MYPROFILE_MY_PRESCRIPTIONS_SEARCH_DOCTOR_ERROR_2'
                                    )}
                                    <a
                                        onClick={() =>
                                            dispatch(actions.setOpenModalAddDoctor(true))
                                        }
                                    >
                                        {getLabelFallback(
                                            'MYPROFILE_MY_PRESCRIPTIONS_SEARCH_DOCTOR_ERROR_2_CLICKED'
                                        )}
                                    </a>
                                </div>
                            </div>
                        ) : getDoctorRequestStatus === 'LOADING' ? (
                            <div className="loading-doctor"></div>
                        ) : (
                            <div className="search-doctor-modal-container-default">
                                {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_SEARCH_DOCTOR')}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default SearchDoctorModal;
